/**
 * HtmlRenderer - Generates complete HTML documents with interactive playback controls
 */

import type { AnimationSequence } from '../../parser/types';
import { SvgBuilder, type SvgBuilderOptions } from './svg/SvgBuilder';

export interface HtmlRendererOptions {
  /**
   * Options passed to the SvgBuilder
   */
  svgOptions?: Partial<SvgBuilderOptions>;
  /**
   * Custom CSS to inject into the document
   */
  customCss?: string;
  /**
   * Title to display in the browser tab
   */
  pageTitle?: string;
}

/**
 * Renders a complete HTML document with SVG animation and playback controls
 */
export class HtmlRenderer {
  private readonly ast: AnimationSequence;
  private readonly options: HtmlRendererOptions;
  private readonly svgBuilder: SvgBuilder;

  constructor(ast: AnimationSequence, options: HtmlRendererOptions = {}) {
    this.ast = ast;
    this.options = options;
    this.svgBuilder = new SvgBuilder(ast, options.svgOptions);
  }

  /**
   * Generates the complete HTML document
   */
  public render(): string {
    const parts: string[] = [];

    parts.push('<!DOCTYPE html>');
    parts.push('<html lang="en">');
    parts.push(this.renderHead());
    parts.push(this.renderBody());
    parts.push('</html>');

    return parts.join('\n');
  }

  /**
   * Renders the HTML head section
   */
  private renderHead(): string {
    const title = this.options.pageTitle ?? this.ast.title;

    return `<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${this.escapeHtml(title)}</title>
  ${this.renderStyles()}
</head>`;
  }

  /**
   * Renders the HTML body section
   */
  private renderBody(): string {
    const svg = this.svgBuilder.build();
    const stepCount = this.svgBuilder.getStepCount();

    return `<body>
  <div class="container">
    <header>
      <h1>${this.escapeHtml(this.ast.title)}</h1>
    </header>

    <div class="animation-container">
      ${svg}
    </div>

    <div class="controls">
      <button id="prevBtn" class="control-btn" aria-label="Previous step">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
          <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
        </svg>
      </button>

      <button id="playBtn" class="control-btn play-btn" aria-label="Play/Pause">
        <svg class="play-icon" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
          <path d="M8 5v14l11-7z"/>
        </svg>
        <svg class="pause-icon" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" style="display: none;">
          <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
        </svg>
      </button>

      <button id="nextBtn" class="control-btn" aria-label="Next step">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
          <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
        </svg>
      </button>

      <div class="step-info">
        <span id="currentStep">0</span> / <span id="totalSteps">${stepCount}</span>
      </div>

      <div class="zoom-controls">
        <button id="zoomOutBtn" class="control-btn zoom-btn" aria-label="Zoom out" title="Zoom out">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14zM7 9h5v1H7z"/>
          </svg>
        </button>

        <button id="zoomResetBtn" class="control-btn zoom-btn" aria-label="Reset zoom" title="Reset zoom (100%)">
          <span class="zoom-level">100%</span>
        </button>

        <button id="zoomInBtn" class="control-btn zoom-btn" aria-label="Zoom in" title="Zoom in">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14zm.5-7h-1v2H7v1h2v2h1v-2h2V9h-2z"/>
          </svg>
        </button>
      </div>
    </div>

    <div class="timeline">
      <input type="range" id="timeline" min="0" max="${stepCount}" value="0" step="1">
    </div>
  </div>

  ${this.renderScript(stepCount)}
</body>`;
  }

  /**
   * Renders the CSS styles
   */
  private renderStyles(): string {
    return `<style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }

    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
      background: #f5f5f5;
      padding: 20px;
    }

    .container {
      max-width: 1200px;
      margin: 0 auto;
      background: white;
      border-radius: 8px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      overflow: hidden;
    }

    header {
      background: #333;
      color: white;
      padding: 20px 30px;
    }

    header h1 {
      font-size: 24px;
      font-weight: 600;
    }

    .animation-container {
      padding: 20px;
      display: flex;
      justify-content: center;
      background: white;
    }

    svg {
      max-width: 100%;
      height: auto;
    }

    .controls {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 10px;
      padding: 20px;
      border-top: 1px solid #e0e0e0;
    }

    .control-btn {
      background: #0066cc;
      color: white;
      border: none;
      border-radius: 50%;
      width: 48px;
      height: 48px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: background 0.2s, transform 0.1s;
    }

    .control-btn:hover {
      background: #0052a3;
    }

    .control-btn:active {
      transform: scale(0.95);
    }

    .control-btn:disabled {
      background: #ccc;
      cursor: not-allowed;
    }

    .play-btn {
      width: 56px;
      height: 56px;
    }

    .step-info {
      font-size: 14px;
      color: #666;
      min-width: 60px;
      text-align: center;
    }

    .zoom-controls {
      display: flex;
      gap: 6px;
      margin-left: 20px;
      padding-left: 20px;
      border-left: 1px solid #e0e0e0;
    }

    .zoom-btn {
      width: 40px;
      height: 40px;
      background: #f5f5f5;
      color: #333;
    }

    .zoom-btn:hover {
      background: #e0e0e0;
    }

    .zoom-btn:disabled {
      background: #f5f5f5;
      opacity: 0.4;
    }

    .zoom-level {
      font-size: 12px;
      font-weight: 600;
    }

    .animation-container {
      overflow: hidden;
      position: relative;
    }

    .animation-container svg {
      transition: transform 0.2s ease;
    }

    .timeline {
      padding: 0 30px 20px;
    }

    #timeline {
      width: 100%;
      height: 6px;
      border-radius: 3px;
      background: #e0e0e0;
      outline: none;
      -webkit-appearance: none;
    }

    #timeline::-webkit-slider-thumb {
      -webkit-appearance: none;
      width: 18px;
      height: 18px;
      border-radius: 50%;
      background: #0066cc;
      cursor: pointer;
    }

    #timeline::-moz-range-thumb {
      width: 18px;
      height: 18px;
      border-radius: 50%;
      background: #0066cc;
      cursor: pointer;
      border: none;
    }

    .step-interactions {
      transition: opacity 0.3s ease;
    }

    .step-interactions.hidden {
      opacity: 0;
      pointer-events: none;
    }

    ${this.options.customCss ?? ''}
  </style>`;
  }

  /**
   * Renders the JavaScript for playback control
   */
  private renderScript(stepCount: number): string {
    return `<script>
    (function() {
      let currentStep = 0;
      const totalSteps = ${stepCount};
      let isPlaying = false;
      let playInterval = null;

      const prevBtn = document.getElementById('prevBtn');
      const playBtn = document.getElementById('playBtn');
      const nextBtn = document.getElementById('nextBtn');
      const timeline = document.getElementById('timeline');
      const currentStepEl = document.getElementById('currentStep');
      const playIcon = document.querySelector('.play-icon');
      const pauseIcon = document.querySelector('.pause-icon');

      // Zoom controls
      const zoomInBtn = document.getElementById('zoomInBtn');
      const zoomOutBtn = document.getElementById('zoomOutBtn');
      const zoomResetBtn = document.getElementById('zoomResetBtn');
      const zoomLevelEl = document.querySelector('.zoom-level');
      const svg = document.querySelector('.animation-container svg');

      let zoomLevel = 1.0;
      const zoomStep = 0.2;
      const minZoom = 0.2;
      const maxZoom = 5.0;

      // Store original viewBox
      const originalViewBox = svg.getAttribute('viewBox').split(' ').map(Number);
      const [origX, origY, origWidth, origHeight] = originalViewBox;

      function updateDisplay() {
        currentStepEl.textContent = currentStep;
        timeline.value = currentStep;

        // Update button states
        prevBtn.disabled = currentStep === 0;
        nextBtn.disabled = currentStep === totalSteps;

        // Show/hide step interactions
        const allSteps = document.querySelectorAll('.step-interactions');
        allSteps.forEach((stepEl, index) => {
          if (index < currentStep) {
            stepEl.classList.remove('hidden');
          } else {
            stepEl.classList.add('hidden');
          }
        });
      }

      function goToStep(step) {
        currentStep = Math.max(0, Math.min(totalSteps, step));
        updateDisplay();
      }

      function nextStep() {
        if (currentStep < totalSteps) {
          goToStep(currentStep + 1);
        } else {
          stop();
        }
      }

      function prevStep() {
        goToStep(currentStep - 1);
      }

      function play() {
        if (currentStep === totalSteps) {
          goToStep(0);
        }
        isPlaying = true;
        playIcon.style.display = 'none';
        pauseIcon.style.display = 'block';
        playInterval = setInterval(() => {
          if (currentStep >= totalSteps) {
            stop();
          } else {
            nextStep();
          }
        }, 2000);
      }

      function pause() {
        isPlaying = false;
        playIcon.style.display = 'block';
        pauseIcon.style.display = 'none';
        if (playInterval) {
          clearInterval(playInterval);
          playInterval = null;
        }
      }

      function stop() {
        pause();
      }

      function togglePlay() {
        if (isPlaying) {
          pause();
        } else {
          play();
        }
      }

      // Event listeners
      prevBtn.addEventListener('click', prevStep);
      nextBtn.addEventListener('click', nextStep);
      playBtn.addEventListener('click', togglePlay);
      timeline.addEventListener('input', (e) => {
        pause();
        goToStep(parseInt(e.target.value, 10));
      });

      // Zoom functions
      function updateZoom() {
        // Calculate new viewBox dimensions based on zoom level
        const newWidth = origWidth / zoomLevel;
        const newHeight = origHeight / zoomLevel;

        // Keep the center point the same
        const centerX = origX + origWidth / 2;
        const centerY = origY + origHeight / 2;

        const newX = centerX - newWidth / 2;
        const newY = centerY - newHeight / 2;

        svg.setAttribute('viewBox', newX + ' ' + newY + ' ' + newWidth + ' ' + newHeight);

        // Update zoom level display
        zoomLevelEl.textContent = Math.round(zoomLevel * 100) + '%';

        // Update button states
        zoomInBtn.disabled = zoomLevel >= maxZoom;
        zoomOutBtn.disabled = zoomLevel <= minZoom;
      }

      function zoomIn() {
        zoomLevel = Math.min(maxZoom, zoomLevel + zoomStep);
        updateZoom();
      }

      function zoomOut() {
        zoomLevel = Math.max(minZoom, zoomLevel - zoomStep);
        updateZoom();
      }

      function resetZoom() {
        zoomLevel = 1.0;
        svg.setAttribute('viewBox', originalViewBox.join(' '));
        zoomLevelEl.textContent = '100%';
        zoomInBtn.disabled = false;
        zoomOutBtn.disabled = false;
      }

      // Zoom event listeners
      zoomInBtn.addEventListener('click', zoomIn);
      zoomOutBtn.addEventListener('click', zoomOut);
      zoomResetBtn.addEventListener('click', resetZoom);

      // Mouse wheel zoom
      const animationContainer = document.querySelector('.animation-container');
      animationContainer.addEventListener('wheel', (e) => {
        e.preventDefault();
        if (e.deltaY < 0) {
          zoomIn();
        } else {
          zoomOut();
        }
      }, { passive: false });

      // Keyboard shortcuts
      document.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') {
          prevStep();
        } else if (e.key === 'ArrowRight') {
          nextStep();
        } else if (e.key === ' ') {
          e.preventDefault();
          togglePlay();
        } else if (e.key === '+' || e.key === '=') {
          e.preventDefault();
          zoomIn();
        } else if (e.key === '-' || e.key === '_') {
          e.preventDefault();
          zoomOut();
        } else if (e.key === '0') {
          e.preventDefault();
          resetZoom();
        }
      });

      // Initialize
      updateDisplay();
    })();
  </script>`;
  }

  /**
   * Escapes HTML special characters
   */
  private escapeHtml(text: string): string {
    const map: Record<string, string> = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    };
    return text.replace(/[&<>"']/g, (char) => map[char] ?? char);
  }
}
